import { XMarkIcon } from "@heroicons/react/20/solid";
import { CloudArrowUpIcon } from "@heroicons/react/24/solid";
import { useEffect, useState, useRef } from "react";
import Image from "../Image/Image";
import ButtonBase from "../Buttons/ButtonBase";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";

interface FileImageInputProps {
  placeHolderText?: string;
  className?: string;
  // eslint-disable-next-line
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  reset?: () => void;
  value?: File | any;
  iconSize?: string;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
  acceptedFileTypes?: string;
  id?: number;
}

function FileImageInput({
  placeHolderText = "Drag and drop file here",
  className = "",
  onChange = () => {},
  value,
  error,
  acceptedFileTypes = "",
  id = 0,
  reset = () => {},
}: FileImageInputProps) {
  // STATE
  const [file, setFile] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // This function helps to avoid issues with browsers behavior.
  const handleReset = () => {
    reset();
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setIsFocused(false);
  };
  useEffect(() => {
    if (typeof value === "string" && value.trim() !== "") {
      setFile(value);
    } else if (value instanceof File) {
      setFile(URL.createObjectURL(value));
    } else {
      setFile("");
    }
  }, [value]);

  // Handle file drop
  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      onChange({
        target: { files },
      } as React.ChangeEvent<HTMLInputElement>);
      setFile(URL.createObjectURL(files[0]));
    }
    setIsFocused(false);
  };

  // Prevent default behavior for drag and drop
  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFocused(true);
  };

  const handleDragLeave = () => {
    setIsFocused(false);
  };
  return (
    <div
      className={`relative flex-1 w-full flex flex-col items-start justify-center focus:outline-none ${
        isFocused ? "border-primary/50 border-2  rounded-lg border-dashed" : ""
      }`}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      {file !== "" ? (
        <ButtonBase
          onClick={handleReset}
          icon={<XMarkIcon className="size-4" />}
          buttonType="tertiary"
          className="  bg-offWhite rounded-full text-body  dark:text-gray absolute -top-2 -left-2"
        />
      ) : null}
      <label
        htmlFor={`logo-dropzone-file-${id}`}
        className={`flex flex-col items-center justify-center ${
          isFocused ? "border-dotted" : "border-2 border-dashed"
        } p-1 ${
          error?.message ? "border-error" : "border-lightGray"
        } w-full rounded-lg ${className}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {!file ? (
          <>
            <CloudArrowUpIcon className="h-20 w-20 text-lightGray" />
            <p className="text-sm font-medium text-subHeading dark:text-offWhite max-sm:text-center">
              {placeHolderText}
            </p>
            <span className="text-sm text-primary dark:text-primaryShade">
              Browse for a file
            </span>
          </>
        ) : (
          <Image
            src={file}
            containerClass="w-36 h-full flex justify-center items-center"
            className="object-contain"
          />
        )}
      </label>
      {error?.message && (
        <p className="text-xs text-error font-medium pl-2 mt-0.5">
          {`${error.message}`}
        </p>
      )}
      <input
        tabIndex={0}
        ref={fileInputRef}
        onChange={onChange}
        type="file"
        name="logo"
        // Need to pass unique id , issue arise when we use this multiple times in one component
        id={`logo-dropzone-file-${id}`}
        className="opacity-0 absolute w-0 h-0"
        accept={acceptedFileTypes}
      />
    </div>
  );
}
export default FileImageInput;
